import { useState, useEffect, useMemo, useRef } from 'react';
import { DynamicFieldGroupProps } from './DynamicFieldGroup.types';
import { H2 } from '../H2/H2';
import { Divider } from '../Divider/Divider';
import { PlusIcon, XCloseModal } from '../../../icons/SvgIcons';
import { Input } from '../Input/Input';
import { RichTextArea } from '../EditableInput/RichTextArea';
import { TextButton } from '../Button/Buttons';
import RadioButtonGroup from '../RadioButton/RadioButtonGroup';

export const DynamicFieldGroup = ({
  title,
  description,
  fields,
  onChange,
  initialValue
}: DynamicFieldGroupProps) => {
  const skipNextInitialValueSyncRef = useRef(false);

  const isDeclarationInlineSection =
    title === 'Conformity Statement' ||
    title === 'Harmonised Standards / Common Specifications';

  const fieldSchemaKey = useMemo(
    () =>
      fields.map((field) => `${field.label}:${field.type || 'text'}`).join('|'),
    [fields]
  );

  const emptyRow = useMemo(
    () => Object.fromEntries(fields.map((field) => [field.label, ''])),
    [fieldSchemaKey]
  );

  const createEmptyRow = () => ({ ...emptyRow });
  const initialValueSignature = useMemo(
    () => JSON.stringify(initialValue ?? []),
    [initialValue]
  );

  const [rows, setRows] = useState<Record<string, string>[]>(
    initialValue && initialValue.length > 0 ? initialValue : [createEmptyRow()]
  );

  useEffect(() => {
    if (skipNextInitialValueSyncRef.current) {
      skipNextInitialValueSyncRef.current = false;
      return;
    }

    if (initialValue && initialValue.length > 0) {
      setRows(initialValue);
    } else if (!initialValue || initialValue.length === 0) {
      setRows([createEmptyRow()]);
    }
  }, [initialValueSignature, emptyRow]);

  const handleChange = (rowIndex: number, field: string, value: string) => {
    const newRows = rows.map((row, index) =>
      index === rowIndex ? { ...row, [field]: value } : row
    );
    setRows(newRows);
    skipNextInitialValueSyncRef.current = true;
    onChange?.(newRows);
  };

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const removeRow = (rowIndex: number) => {
    const newRows = rows.filter((_, index) => index !== rowIndex);
    setRows(newRows);
    skipNextInitialValueSyncRef.current = true;
    onChange?.(newRows);
  };

  const renderField = (
    field: any,
    rowIndex: number,
    row: Record<string, string>
  ) => {
    const fieldKey = field.label;

    if (field.type === 'radiobutton' && field.label === 'Likelihood') {
      const severityField = fields.find(
        (field) => field.type === 'radiobutton' && field.label === 'Severity'
      );

      const initialRiskLevelField = fields.find(
        (field) =>
          field.type === 'radiobutton' && field.label === 'Initial Risk Level'
      );

      const riskFields = [field, severityField, initialRiskLevelField];

      return (
        <div className="flex flex-row gap-4">
          {riskFields.map((riskField, index) => (
            <div
              key={riskField.label}
              className={`flex flex-1 ${index === 1 ? 'justify-center' : index === 2 ? 'justify-end' : ''}`}
            >
              <RadioButtonGroup
                title={riskField.label}
                name={`${riskField.label}_${rowIndex}`}
                options={riskField.options || []}
                selectedValue={row[riskField.label] || ''}
                onChange={(value) =>
                  handleChange(rowIndex, riskField.label, value)
                }
                direction="horizontal"
              />
            </div>
          ))}
        </div>
      );
    }

    if (
      field.type === 'radiobutton' &&
      (field.label === 'Severity' || field.label === 'Initial Risk Level')
    ) {
      return null;
    }

    switch (field.type) {
      case 'text':
      default:
        return (
          <Input
            key={field.label}
            id={field.label}
            label={field.label}
            maxLength={255}
            placeholder={field.placeholder}
            value={row[field.label] || ''}
            onChange={(e) =>
              handleChange(rowIndex, field.label, e.target.value)
            }
            labelClassName="label"
            inputClassName="input"
            containerClassName="flex-1"
          />
        );

      case 'richtext':
        return (
          <div key={fieldKey} className="flex-1">
            <RichTextArea
              label={field.label}
              placeholder={field.placeholder || ''}
              onChange={(value) => handleChange(rowIndex, fieldKey, value)}
              value={row[fieldKey] || ''}
            />
          </div>
        );

      case 'radiobutton':
        return (
          <div key={fieldKey} className="flex-1">
            <RadioButtonGroup
              title={field.label}
              name={`${fieldKey}_${rowIndex}`}
              options={field.options || []}
              selectedValue={row[fieldKey] || ''}
              onChange={(value) => handleChange(rowIndex, fieldKey, value)}
              direction="horizontal"
            />
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
      <H2 title={title} textSize="text-base" />
      <Divider classes="-my-2" />
      <p className="text-gray-900">{description}</p>

      {rows.map((row, index) => (
        <div key={index} className="flex flex-col gap-4 relative pr-4 mb-6">
          <div className="flex flex-row items-center justify-between">
            <button
              onClick={() => removeRow(index)}
              id={`dynamic-field-group-remove-row-button-${index}`}
              data-testid={`dynamic-field-group-remove-row-button-${index}`}
              className="absolute -right-2.5 bottom-2.5 cursor-pointer"
            >
              {rows.length > 1 ? XCloseModal('#DADADD') : null}
            </button>
          </div>
          <div
            className={
              isDeclarationInlineSection
                ? 'flex flex-row gap-4'
                : 'flex flex-col gap-4'
            }
          >
            {fields.map((field) => renderField(field, index, row))}
          </div>
        </div>
      ))}

      <div>
        <TextButton onClick={addRow} icon={PlusIcon()}>
          Add Another
        </TextButton>
      </div>
    </div>
  );
};
